import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import i18n from 'scoreboard/components/i18n'
import _ from 'lodash'
import settingsHelper from './settings-helper'
import enums from 'scoreboard/components/enums'

import GeographicMapLevel from 'scoreboard/charts/model/geographic-map-level'
const { 
    EARTH3D,
    EARTH2D,
    CONTINENT,
    COUNTRY,
    STATE_PROVINCE,
    COUNTY,
    POSTAL_CODE,
    CITY,
 } = GeographicMapLevel

import Tooltip from 'scoreboard/components/tooltip'

const furthestValidLevel = {
	[EARTH3D]: 3,
	[EARTH2D]: 2,
	[CONTINENT]: 1,
	[COUNTRY]: 2,
	'country-postal': 1,
	[STATE_PROVINCE]: 1,
	[COUNTY]: 1,
	[POSTAL_CODE]: 1,
	[CITY]: null
}

const MapLevelsMenu = BootslideMenu.extend({

	model: null,

	menu: function() {
		const mapLevels = enums.toOptionData('geographicMapLevel')
		const geographicField = _.defaultTo(this.model.datasetGeographicField(),
			{ dataType: COUNTRY })
		const validMapLevels = settingsHelper.getValidMapLevels(geographicField)
		return {
			label: i18n.get('charts.chart.settingType.mapLevels'),
			width: 200,
			target: _.map(mapLevels, level => {
				const disabled = !_.includes(validMapLevels, level.id)
				const menu = this
				return {
					type: 'toggle',
					label: level.label,
					contentView: BootslideMenu.ToggleProperty.extend({

						model: null,

						initialize: function (options = {}) {
							BootslideMenu.ToggleProperty.prototype.initialize.apply(this, arguments)
							this.menu = menu
						},

						property: {
							label: level.label,
							name: `${_.camelCase(level.id)}Enabled`,
							disabled,
							classNames: disabled ? 'is-disabled' : ''
						},

						toggle: function() {
							if (disabled) {
								this.menu.invalidLevelTooltip?.remove()
								this.menu.invalidLevelTooltip = new Tooltip({
									attachTo: this.$el,
									content: i18n.get('charts.chart.settingType.mapLevels.invalidLevelTooltip'),
									placement: 'left',
									arrow: 'top'
								}).render()
								return
							}

							if (_.size(this.model.getEnabledMapLevels()) === 1
								&& !!this.val()) {
								return
							}

							BootslideMenu.ToggleProperty.prototype.toggle.apply(this, arguments)
							this.toggleMapLevels()
						},

						toProp: (l) => `${_.camelCase(l)}Enabled`,

						toggleMapLevels: function() {
							const designer = this.model
							const changed = _(mapLevels)
								.map(l => ({
									level: l.id,
									property: this.toProp(l.id),
									value: designer.changed[this.toProp(l.id)]
								}))
								.filter(l => !_.isNil(l.value))
								.value()[0]

							const field = designer.datasetGeographicField()
							if (_.isNil(field)) {
								// if this is placeholder data, still toggle earth
								// levels appropriately
								this.toggleEarthLevels(changed, designer)
								return
							}

							const indices = getLevelIndices(changed, designer,
								field, validMapLevels)
							if (_.isNil(indices)) return

							// after gathering all the necessary information for
							// the various map level indices, toggle on/off levels
							this.toggleLevels({
								changed,
								designer,
								args: _.extend({}, { designer }, indices)
							})
						},

						/**
						 * with the index information, toggles on/off map levels
						 * if necessary
						 */
						toggleLevels: function({ changed, designer, args }) {
							if (!changed.value) {
								// if we just turned off a map level, we will
								// potentially turn off all below it if there is a
								// large enough gap
								this.disableLevelsBelow(args)
							} else if (!!changed.value) {
								// if we just turned on a map level, we will
								// potentially turn on all in between it and the
								// closest above if there is a large enough gap
								enableLevelsBetween(_.extend(args, { validMapLevels }))
							}
							this.toggleEarthLevels(changed, designer)
						},

						toggleEarthLevels: function(changed, designer) {
							// only 2D or 3D earth can be on at a time
							// but not both
							if (changed.level === EARTH2D && !!changed.value) {
								designer.set('earth3DEnabled', false)
							}
							if (changed.level === EARTH3D && !!changed.value) {
								designer.set('earth2DEnabled', false)
							}
						},

						/**
						 * Toggles the other map levels when one level is turned
						 * off.
						 */
						disableLevelsBelow: function({designer, firstIndex,
														 lastIndex, nextIndex }) {
							// if turning off this level left too big of a gap
							// between the closest level above and the closest level
							// below, turn off all the levels underneath the level
							// we just turned off as well
							let disableAllBelow = true
							for (let i = firstIndex + 1; i <= nextIndex; i ++) {
								const property = this.toProp(validMapLevels[i])
								if (!!designer.get(property)) {
									disableAllBelow = false
								}
							}
							if (disableAllBelow) {
								const disabledLevels = validMapLevels.slice(
									lastIndex + 1, _.size(validMapLevels))
								_.each(disabledLevels, dl =>
									designer.set(this.toProp(dl), false))
							}
						}
					})
				}
			})
		}
	},

	autoRemove: true
})

/**
 * Toggles the other map levels when one level is turned
 * on.
 */
const enableLevelsBetween = ({ designer, firstIndex, lastIndex, nextIndex, validMapLevels }) => {
	// if turning on this level resulted in too big of a gap
	// between the closest level above or the closest level
	// below and this level, turn on all the levels in
	// between
	const enableAllBetween = nextIndex < lastIndex
	if (enableAllBetween) {
		const enabledLevels = validMapLevels.slice(firstIndex + 1, lastIndex)
		_.each(enabledLevels, dl => {
			if (dl !== EARTH2D) {
				designer.set(`${_.camelCase(dl)}Enabled`, true)
			}
		})
	}
}

/**
 * gets indices of various map levels that we need in order
 * to determine which map levels, if any, need to be
 * toggled on/off
 *
 * firstIndex: the index of the closest map level above
 * the one that was just toggled. Or if there are none,
 * the index of the map level that was just toggled
 *
 * lastIndex: the index of the map level that was just
 * toggled. Or if there were no levels enabled above the
 * level, the index of the closest level below
 *
 * nextIndex: the index of the first necessarily enabled
 * map level from the firstIndex
 */
const getLevelIndices = (changed, designer, field, validMapLevels) => {
	// get the level that was just turned on/off and its
	// index
	const level = changed.level
	const levelIndex = _.indexOf(validMapLevels, level)

	// get the closest level above and its index
	const levelsAbove = validMapLevels
		.slice(0, levelIndex)
		.reverse()
	let closest = _.find(levelsAbove, l =>
		!!designer.get(`${_.camelCase(l)}Enabled`))

	// if there is not a level above, look below to make
	// sure we aren't leaving a gap there
	if (_.isNil(closest)) {
		// we can't get into trouble turning off the highest
		// level
		if (!changed.value) return

		const levelsBelow = validMapLevels
			.slice(levelIndex + 1, _.size(validMapLevels))
		closest = _.find(levelsBelow, l =>
			!!designer.get(`${_.camelCase(l)}Enabled`))
	}
	const closestIndex = _.indexOf(validMapLevels, closest)

	// these will be the map level we just turned on/off
	// and the nearest level that is on depending on which
	// which is higher up
	const firstIndex = _.min([levelIndex, closestIndex])
	const lastIndex = _.max([levelIndex, closestIndex])

	// get the furthest valid map level from the first level
	let first = validMapLevels[firstIndex]
	if (field.dataType === POSTAL_CODE
		&& first === COUNTRY) {
		first += '-postal'
	}
	const nextIndex = firstIndex + furthestValidLevel[first]
	if (nextIndex >= _.size(validMapLevels)) return

	return { firstIndex, lastIndex, nextIndex }
}

export default MapLevelsMenu
export { enableLevelsBetween }
export { getLevelIndices }
MapLevelsMenu.__filename__ = 'charts/edit/map-levels-menu.js'
